import { zodResolver } from '@hookform/resolvers/zod'
import moment from 'moment'
import { RadioGroup as RadioGroupPrimitive } from 'radix-ui'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SettingsFormFooter } from '@/containers/Settings/SettingsLayout'
import API from '@/lib/api'
import { cn } from '@/lib/utils'
import { DocumentType } from '@/types/DocumentType'

import { DocumentSequenceFormSchema } from './schema'

type DocumentSequenceFormValues = z.infer<typeof DocumentSequenceFormSchema>

export interface DocumentSequenceFormProps {
  type: DocumentType
  id?: string
  defaultValues?: DocumentSequenceFormValues
  onSaveSuccess?: () => void
}

/** Pilulkový výběr jedné hodnoty (radio skupina s klávesovým ovládáním). */
const Choice = ({
  value,
  onChange,
  options,
  label,
}: {
  value: string | undefined
  onChange: (value: string) => void
  options: { value: string; label: string }[]
  label: string
}) => (
  <RadioGroupPrimitive.Root
    value={value ?? ''}
    onValueChange={onChange}
    aria-label={label}
    className="inline-flex flex-wrap gap-1"
  >
    {options.map((o) => (
      <RadioGroupPrimitive.Item
        key={o.value || 'none'}
        value={o.value}
        className={cn(
          'inline-flex h-8 items-center rounded-md border px-3 text-sm tabular transition-colors',
          'text-muted-foreground hover:bg-muted hover:text-foreground',
          'data-[state=checked]:border-foreground data-[state=checked]:bg-foreground data-[state=checked]:text-background',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
        )}
      >
        {o.label}
      </RadioGroupPrimitive.Item>
    ))}
  </RadioGroupPrimitive.Root>
)

export const DocumentSequenceForm = ({
  type,
  id,
  defaultValues,
  onSaveSuccess,
}: DocumentSequenceFormProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'settings.documentSequences.form',
  })

  const methods = useForm<DocumentSequenceFormValues>({
    resolver: zodResolver(DocumentSequenceFormSchema) as any,
    defaultValues,
  })

  const {
    formState: { errors },
    handleSubmit,
    register,
  } = methods
  const [isSubmitting, setIsSubmitting] = useState(false)

  const onSuccess = async (values: DocumentSequenceFormValues) => {
    setIsSubmitting(true)

    const body = {
      prefix: values.prefix,
      format: `${values.year}${values.month}${values.suffix}`,
      sequenceLength: values.exp,
      default: values.default,
    }

    try {
      if (id) {
        await API.post(`/api/v2/document-sequences/${id}`, body)
      } else {
        await API.post(`/api/v2/document-sequences`, { type, ...body })
      }
      toast.success(t('success.title'), {
        duration: 3000,
        closeButton: true,
      })
      onSaveSuccess?.()
    } catch {
      toast.error('Číselnou řadu se nepodařilo uložit.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const now = moment()
  const watch = methods.watch()
  const preview = `${watch.prefix ?? ''}${watch.year ? now.format(watch.year === 'Y' ? 'YYYY' : 'YY') : ''}${watch.month ? now.format('MM') : ''}${watch.suffix ?? ''}${'1'.padStart(watch.exp?.length ? watch.exp.length - 1 : 3, '0')}`

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSuccess)}>
        <div className="mb-6 rounded-lg border bg-muted px-5 py-4">
          <p className="text-xs text-muted-foreground">Příští doklad dostane číslo</p>
          <p className="mt-1 text-2xl font-medium tracking-[-0.02em] tabular">{preview}</p>
        </div>

        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor={`${type}-prefix`}>{t('prefix.label')}</Label>
              <Input
                id={`${type}-prefix`}
                placeholder="Např. FV"
                {...register('prefix')}
                maxLength={3}
              />
              {errors.prefix && <p className="text-sm text-destructive">{errors.prefix.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor={`${type}-suffix`}>{t('suffix.label')}</Label>
              <Input
                id={`${type}-suffix`}
                placeholder="Nepovinné"
                {...register('suffix')}
                maxLength={3}
              />
              {errors.suffix && <p className="text-sm text-destructive">{errors.suffix.message}</p>}
            </div>
          </div>

          <FormField
            control={methods.control}
            name="year"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('year.label')}</FormLabel>
                <FormControl>
                  <Choice
                    label={t('year.label')}
                    value={field.value}
                    onChange={field.onChange}
                    options={[
                      { value: 'Y', label: now.format('YYYY') },
                      { value: 'y', label: now.format('YY') },
                      { value: '', label: t('year.options.noYear') },
                    ]}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={methods.control}
            name="month"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('month.label')}</FormLabel>
                <FormControl>
                  <Choice
                    label={t('month.label')}
                    value={field.value}
                    onChange={field.onChange}
                    options={[
                      { value: 'm', label: now.format('MM') },
                      { value: '', label: t('month.options.noMonth') },
                    ]}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={methods.control}
            name="exp"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('exp.label')}</FormLabel>
                <FormControl>
                  <Choice
                    label={t('exp.label')}
                    value={field.value}
                    onChange={field.onChange}
                    options={[
                      { value: '100', label: '100' },
                      { value: '1000', label: '1 000' },
                      { value: '10000', label: '10 000' },
                      { value: '100000', label: '100 000' },
                    ]}
                  />
                </FormControl>
                <p className="text-sm text-muted-foreground">Podle toho se určí počet číslic pořadového čísla.</p>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <input {...register('default', { value: defaultValues?.default })} type="hidden" />

        <SettingsFormFooter>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Ukládám' : t('submit')}
          </Button>
        </SettingsFormFooter>
      </form>
    </FormProvider>
  )
}
